import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import {
  useGetSimpleOutboundClientsQuery,
  type ComposeRecipient,
  type SimpleOutboundClientOption,
} from '../grid/api/simpleOutboundApi';
import { extractErrorMessage } from '../../utils/errorUtils';
import localStorageService, { LocalStorageKeys } from '../../services/localStorageService';
import {
  buildClientAddressOptions,
  createSelectedClientDraft,
  defaultSelectedAddressKeys,
  draftsToComposeRecipients,
  countRecipientAddresses,
  formatRecipientsSummary,
  normalizeStoredComposeSelection,
  reconcileSelectedDrafts,
  storedDraftsToSelectedById,
  toStoredComposeSelection,
  type SelectedClientDraft,
  type StoredComposeClientSelection,
} from './composeRecipientUtils';
import type { ClientEmailSource } from '../grid/components/dialogs/financeEmailComposeUtils';

type SelectClientForComposeDialogProps = {
  open: boolean;
  onClose: () => void;
  onContinue: (recipients: ComposeRecipient[]) => void;
};

const sortDraftsByName = (drafts: SelectedClientDraft[]) =>
  [...drafts].sort((a, b) =>
    a.clientName.localeCompare(b.clientName, undefined, { sensitivity: 'base' })
  );

const loadStoredSelection = (): StoredComposeClientSelection | null => {
  try {
    return normalizeStoredComposeSelection(
      localStorageService.get(LocalStorageKeys.CachedFinanceComposeClientSelection)
    );
  } catch {
    return null;
  }
};

const persistSelection = (
  preferredSource: ClientEmailSource,
  selectedById: Record<number, SelectedClientDraft>
) => {
  localStorageService.save(
    LocalStorageKeys.CachedFinanceComposeClientSelection,
    toStoredComposeSelection(preferredSource, selectedById)
  );
};

export const SelectClientForComposeDialog = ({
  open,
  onClose,
  onContinue,
}: SelectClientForComposeDialogProps) => {
  const [search, setSearch] = useState('');
  const [preferredSource, setPreferredSource] = useState<ClientEmailSource>(() => {
    return loadStoredSelection()?.preferredSource ?? 'finance';
  });
  const [selectedById, setSelectedById] = useState<Record<number, SelectedClientDraft>>(() => {
    const stored = loadStoredSelection();
    return stored ? storedDraftsToSelectedById(stored.drafts) : {};
  });
  const [localError, setLocalError] = useState<string | null>(null);

  const clientsQuery = useGetSimpleOutboundClientsQuery(undefined, { skip: !open });

  const clients = useMemo(() => clientsQuery.data ?? [], [clientsQuery.data]);
  const clientsReady = clientsQuery.isSuccess || clientsQuery.isError;

  // Rebuild names/emails from authorized GetClients only — never render stored contact PII.
  const reconciledSelectedById = useMemo(() => {
    if (!clientsReady) {
      return {};
    }
    return reconcileSelectedDrafts(selectedById, clients);
  }, [selectedById, clients, clientsReady]);

  const selectedDrafts = useMemo(
    () => sortDraftsByName(Object.values(reconciledSelectedById)),
    [reconciledSelectedById]
  );
  const readyRecipients = useMemo(
    () => draftsToComposeRecipients(selectedDrafts),
    [selectedDrafts]
  );
  const readyAddressCount = useMemo(
    () => countRecipientAddresses(readyRecipients),
    [readyRecipients]
  );
  const recipientsSummary = formatRecipientsSummary(readyRecipients.length, readyAddressCount);
  const clientsWithoutAddress = useMemo(
    () => selectedDrafts.filter((draft) => buildClientAddressOptions(draft).length === 0),
    [selectedDrafts]
  );
  const clientsMissingSelection = useMemo(
    () =>
      selectedDrafts.filter(
        (draft) =>
          buildClientAddressOptions(draft).length > 0 && draft.selectedAddressKeys.length === 0
      ),
    [selectedDrafts]
  );

  const filteredClients = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return clients;
    }

    return clients.filter((client) => {
      const name = (client.name || '').toLowerCase();
      const finance = (client.financeEmail || '').toLowerCase();
      const relation = (client.clientEmail || '').toLowerCase();
      return name.includes(query) || finance.includes(query) || relation.includes(query);
    });
  }, [clients, search]);

  const loadError =
    localError ||
    (clientsQuery.isError
      ? extractErrorMessage(clientsQuery.error, 'Failed to load clients.')
      : null);

  useEffect(() => {
    persistSelection(preferredSource, selectedById);
  }, [preferredSource, selectedById]);

  const handleClose = () => {
    setSearch('');
    setLocalError(null);
    onClose();
  };

  const upsertDraft = (client: SimpleOutboundClientOption) => {
    setSelectedById((current) => {
      if (current[client.id]) {
        return current;
      }
      return {
        ...current,
        [client.id]: createSelectedClientDraft(client, preferredSource),
      };
    });
  };

  const removeDraft = (clientId: number) => {
    setSelectedById((current) => {
      const next = { ...current };
      delete next[clientId];
      return next;
    });
  };

  const toggleClient = (client: SimpleOutboundClientOption) => {
    setLocalError(null);
    if (selectedById[client.id]) {
      removeDraft(client.id);
      return;
    }
    upsertDraft(client);
  };

  const toggleAddress = (clientId: number, addressKey: string) => {
    setLocalError(null);
    setSelectedById((current) => {
      const draft = reconciledSelectedById[clientId] ?? current[clientId];
      if (!draft) {
        return current;
      }

      const exists = draft.selectedAddressKeys.includes(addressKey);
      const selectedAddressKeys = exists
        ? draft.selectedAddressKeys.filter((key) => key !== addressKey)
        : [...draft.selectedAddressKeys, addressKey];

      return {
        ...current,
        [clientId]: { ...draft, selectedAddressKeys },
      };
    });
  };

  const selectAllVisible = () => {
    setLocalError(null);
    setSelectedById((current) => {
      const next = { ...current };
      for (const client of filteredClients) {
        if (!next[client.id]) {
          next[client.id] = createSelectedClientDraft(client, preferredSource);
        }
      }
      return next;
    });
  };

  const clearSelection = () => {
    setLocalError(null);
    setSelectedById({});
    localStorageService.remove(LocalStorageKeys.CachedFinanceComposeClientSelection);
  };

  const applyPreferredToSelected = (preferred: ClientEmailSource) => {
    setPreferredSource(preferred);
    setSelectedById((current) => {
      const source = clientsReady ? reconcileSelectedDrafts(current, clients) : current;
      const next: Record<number, SelectedClientDraft> = {};
      for (const draft of Object.values(source)) {
        next[draft.clientId] = {
          ...draft,
          selectedAddressKeys: defaultSelectedAddressKeys(draft, preferred),
        };
      }
      return next;
    });
  };

  const handleContinue = () => {
    if (selectedDrafts.length === 0) {
      setLocalError('Select at least one client to continue.');
      return;
    }

    if (clientsWithoutAddress.length > 0) {
      setLocalError(
        `These clients have no email addresses: ${clientsWithoutAddress
          .map((item) => item.clientName)
          .join(', ')}.`
      );
      return;
    }

    if (clientsMissingSelection.length > 0) {
      setLocalError(
        `Pick at least one address for: ${clientsMissingSelection
          .map((item) => item.clientName)
          .join(', ')}.`
      );
      return;
    }

    if (readyRecipients.length === 0) {
      setLocalError('Select at least one recipient address.');
      return;
    }

    persistSelection(preferredSource, selectedById);
    setSearch('');
    setLocalError(null);
    onContinue(readyRecipients);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        New email — select clients
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loadError && (
          <Alert severity="error" sx={{ m: 2, mb: 0 }}>
            {loadError}
          </Alert>
        )}

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          sx={{ minHeight: { xs: 420, md: 480 }, maxHeight: { md: '70vh' } }}
        >
          <Box
            sx={{
              width: { xs: '100%', md: '46%' },
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              borderRight: { md: '1px solid' },
              borderBottom: { xs: '1px solid', md: 'none' },
              borderColor: 'divider',
            }}
          >
            <Stack spacing={1.25} sx={{ p: 2, pb: 1 }}>
              <TextField
                size="small"
                fullWidth
                autoFocus
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by name or email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: clientsQuery.isFetching ? (
                    <InputAdornment position="end">
                      <CircularProgress size={16} />
                    </InputAdornment>
                  ) : undefined,
                }}
              />

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel id="preferred-email-source-label">Prefer</InputLabel>
                  <Select
                    labelId="preferred-email-source-label"
                    label="Prefer"
                    value={preferredSource}
                    onChange={(event) =>
                      applyPreferredToSelected(event.target.value as ClientEmailSource)
                    }
                  >
                    <MenuItem value="finance">Finance email</MenuItem>
                    <MenuItem value="client">Client email</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  onClick={selectAllVisible}
                  disabled={filteredClients.length === 0}
                >
                  Select all
                </Button>
                <Button
                  size="small"
                  onClick={clearSelection}
                  disabled={selectedDrafts.length === 0}
                >
                  Clear
                </Button>
              </Stack>

              <Typography variant="caption" color="text.secondary">
                {filteredClients.length} shown · {Object.keys(selectedById).length} selected
              </Typography>
            </Stack>

            <List dense disablePadding sx={{ flex: 1, overflow: 'auto', px: 0.5 }}>
              {filteredClients.map((client) => {
                const checked = Boolean(selectedById[client.id]);
                const addressCount = buildClientAddressOptions(client).length;
                const secondary =
                  addressCount > 0
                    ? [
                        client.financeEmail?.trim()
                          ? `Finance: ${client.financeEmail.trim()}`
                          : null,
                        client.clientEmail?.trim() ? `Client: ${client.clientEmail.trim()}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    : 'No email addresses';

                return (
                  <ListItemButton
                    key={client.id}
                    dense
                    selected={checked}
                    onClick={() => toggleClient(client)}
                    sx={{ alignItems: 'flex-start', borderRadius: 1, mx: 0.5, mb: 0.25 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={checked}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={client.name || `Client #${client.id}`}
                      secondary={secondary}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: checked ? 600 : 500 }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        sx: {
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          color: addressCount === 0 ? 'warning.main' : 'text.secondary',
                        },
                      }}
                    />
                  </ListItemButton>
                );
              })}

              {!clientsQuery.isFetching && filteredClients.length === 0 && (
                <Box sx={{ px: 2, py: 4 }}>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    No clients match your search.
                  </Typography>
                </Box>
              )}
            </List>
          </Box>

          <Box
            sx={{
              width: { xs: '100%', md: '54%' },
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'action.hover' : 'grey.50'),
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, py: 1.5 }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                Recipients ({recipientsSummary})
              </Typography>
              {clientsMissingSelection.length + clientsWithoutAddress.length > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  icon={<WarningIcon />}
                  label={`${clientsMissingSelection.length + clientsWithoutAddress.length} need attention`}
                />
              )}
            </Stack>
            <Divider />

            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {selectedDrafts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {!clientsReady && Object.keys(selectedById).length > 0
                    ? 'Restoring saved selection…'
                    : 'Select one or more clients on the left. Each selected client will receive a separate email, so Postmark tracks delivery per recipient and addresses stay private.'}
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {selectedDrafts.map((draft) => {
                    const options = buildClientAddressOptions(draft);
                    const selectedKeys = new Set(draft.selectedAddressKeys);

                    return (
                      <Box
                        key={draft.clientId}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          spacing={1}
                          sx={{ mb: 0.75 }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700} noWrap>
                              {draft.clientName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {options.length === 0
                                ? 'No addresses on file'
                                : `${draft.selectedAddressKeys.length} of ${options.length} addresses selected`}
                            </Typography>
                          </Box>
                          <Tooltip title="Remove client">
                            <IconButton
                              size="small"
                              aria-label={`Remove ${draft.clientName}`}
                              onClick={() => removeDraft(draft.clientId)}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>

                        {options.length === 0 ? (
                          <Alert severity="warning" sx={{ py: 0 }}>
                            Add a finance or client email in CRM before sending.
                          </Alert>
                        ) : (
                          <Stack spacing={0.25}>
                            {options.map((option) => (
                              <FormControlLabel
                                key={option.key}
                                sx={{
                                  m: 0,
                                  alignItems: 'flex-start',
                                  '& .MuiFormControlLabel-label': { pt: 0.75 },
                                }}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={selectedKeys.has(option.key)}
                                    onChange={() => toggleAddress(draft.clientId, option.key)}
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                                      {option.email}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {option.kind === 'finance' ? 'Finance email' : 'Client email'}
                                    </Typography>
                                  </Box>
                                }
                              />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          {readyRecipients.length > 0
            ? `${readyRecipients.length} separate email${readyRecipients.length === 1 ? '' : 's'} will be created · ${readyAddressCount} address${readyAddressCount === 1 ? '' : 'es'}`
            : 'No ready recipients yet'}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={readyRecipients.length === 0}
          >
            Continue
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default SelectClientForComposeDialog;
